import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../services/firebase';

// ── Company Config ─────────────────────────────────────────────────────────────
function CompanySection({ onSaved }) {
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'company'), snap => {
      if (snap.exists()) {
        const d = snap.data();
        setCurrent(d);
        setName(d.name || '');
        setShortName(d.shortName || '');
        setLogoPreview(d.logoUrl || null);
      }
    });
    return unsub;
  }, []);

  const handleSave = async () => {
    if (!name.trim()) { setMsg('El nombre es obligatorio'); return; }
    setSaving(true);
    setMsg('');
    try {
      let logoUrl = current?.logoUrl || null;
      if (logoFile) {
        const ext = logoFile.name.split('.').pop() || 'png';
        const storageRef = ref(storage, `company/logo.${ext}`);
        const snap = await uploadBytes(storageRef, logoFile);
        logoUrl = await getDownloadURL(snap.ref);
      }
      await setDoc(doc(db, 'config', 'company'), {
        name: name.trim(),
        shortName: shortName.trim() || name.trim().split(' ')[0],
        logoUrl,
        updatedAt: serverTimestamp(),
      });
      setMsg('✓ Configuración guardada');
      setLogoFile(null);
      if (onSaved) onSaved();
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    }
    setSaving(false);
  };

  const inputCls = 'w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500/50';

  return (
    <div className="space-y-4">
      <h3 className="text-white/70 font-medium text-sm uppercase tracking-wide">Empresa</h3>
      <div className="flex items-start gap-4">
        {/* Logo */}
        <div>
          <div className="h-20 w-20 rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden bg-white/5 relative group cursor-pointer"
            onClick={() => document.getElementById('company-logo-upload')?.click()}>
            {logoPreview
              ? <img src={logoPreview} alt="Logo" className="h-full w-full object-contain p-1" />
              : <span className="text-white/25 text-xs text-center px-2">Logo</span>}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white text-xs font-medium">Cambiar</span>
            </div>
          </div>
          <input id="company-logo-upload" type="file" accept="image/*" className="hidden"
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); }
            }} />
          <p className="text-white/25 text-[10px] mt-1 text-center">PNG/SVG/JPG</p>
        </div>
        {/* Fields */}
        <div className="flex-1 space-y-3">
          <div>
            <label className="text-white/50 text-xs uppercase tracking-wide">Nombre de la empresa</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="New in the Box" className={inputCls} />
          </div>
          <div>
            <label className="text-white/50 text-xs uppercase tracking-wide">Nombre corto (pantalla PIN)</label>
            <input value={shortName} onChange={e => setShortName(e.target.value)} placeholder="NITB" className={inputCls} />
          </div>
        </div>
      </div>
      {msg && <p className={`text-xs ${msg.startsWith('✓') ? 'text-emerald-400' : 'text-red-400'}`}>{msg}</p>}
      <button onClick={handleSave} disabled={saving}
        className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
        {saving ? 'Guardando…' : 'Guardar configuración de empresa'}
      </button>
    </div>
  );
}

// ── Shopify Config ─────────────────────────────────────────────────────────────
function ShopifySection() {
  const [status, setStatus] = useState(null);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [shopDomain, setShopDomain] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [savingApp, setSavingApp] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'shopify'), snap => {
      if (snap.exists()) setStatus(snap.data());
      else setStatus(null);
    });
    return unsub;
  }, []);

  const handleSaveApp = async () => {
    if (!clientId.trim() || !clientSecret.trim()) { setMsg('Ingresa Client ID y Client Secret'); return; }
    setSavingApp(true);
    setMsg('');
    try {
      await setDoc(doc(db, 'config', 'shopifyApp'), {
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        updatedAt: serverTimestamp(),
      });
      setMsg('✓ Credenciales guardadas');
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    }
    setSavingApp(false);
  };

  const handleConnect = () => {
    if (!clientId.trim()) { setMsg('Primero guarda el Client ID'); return; }
    const shop = shopDomain.trim().replace(/^https?:\/\//, '').replace(/\/$/, '');
    if (!shop) { setMsg('Ingresa el dominio de la tienda'); return; }
    // Direct Cloud Function URL — works without Cloud Run hosting rewrite
    const redirect = 'https://us-central1-workers-wgknib.cloudfunctions.net/shopifyCallback';
    const scopes = 'read_orders,read_all_orders,read_products,read_inventory,read_customers';
    const state = Math.random().toString(36).slice(2);
    sessionStorage.setItem('shopify_oauth_state', state);
    window.location.href = `https://${shop}/admin/oauth/authorize?client_id=${clientId.trim()}&scope=${scopes}&redirect_uri=${encodeURIComponent(redirect)}&state=${state}`;
  };

  const inputCls = 'w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500/50';

  return (
    <div className="space-y-4">
      <h3 className="text-white/70 font-medium text-sm uppercase tracking-wide">Integración Shopify</h3>

      {/* Connection status */}
      {status ? (
        <div className="bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-4 py-3 text-sm">
          <p className="text-emerald-400 font-medium">✓ Conectado</p>
          <p className="text-white/50 text-xs mt-0.5">Tienda: {status.storeHint || status.storeUrl}</p>
        </div>
      ) : (
        <div className="bg-amber-500/10 border border-amber-500/25 rounded-lg px-4 py-3 text-xs text-amber-300">
          Sin configurar — ingresa tus credenciales de Shopify Partners
        </div>
      )}

      {/* OAuth credentials */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wide">Client ID</label>
          <input value={clientId} onChange={e => setClientId(e.target.value)} placeholder="Shopify Partners → API credentials" className={inputCls} />
        </div>
        <div>
          <label className="text-white/50 text-xs uppercase tracking-wide">Client Secret</label>
          <div className="relative">
            <input value={clientSecret} onChange={e => setClientSecret(e.target.value)} type={showSecret ? 'text' : 'password'}
              placeholder="Client Secret" className={`${inputCls} pr-8`} />
            <button type="button" onClick={() => setShowSecret(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 text-xs">
              {showSecret ? '🙈' : '👁'}
            </button>
          </div>
        </div>
      </div>
      <div>
        <label className="text-white/50 text-xs uppercase tracking-wide">Dominio de la tienda</label>
        <input value={shopDomain} onChange={e => setShopDomain(e.target.value)} placeholder="tu-tienda.myshopify.com" className={inputCls} />
      </div>

      {msg && <p className={`text-xs ${msg.startsWith('✓') ? 'text-emerald-400' : 'text-red-400'}`}>{msg}</p>}

      <div className="flex gap-2">
        <button onClick={handleSaveApp} disabled={savingApp}
          className="flex-1 py-2.5 bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-lg text-sm transition-colors disabled:opacity-50">
          {savingApp ? 'Guardando…' : 'Guardar credenciales'}
        </button>
        <button onClick={handleConnect}
          className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
          Conectar tienda →
        </button>
      </div>
    </div>
  );
}

// ── Main modal ─────────────────────────────────────────────────────────────────
export default function AppSettings({ isOpen, onClose }) {
  const [tab, setTab] = useState('company');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-gray-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
          <h2 className="text-white font-bold text-lg">⚙️ Configuración</h2>
          <button onClick={onClose} className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors">✕</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 shrink-0">
          {[['company', '🏢 Empresa'], ['shopify', '🛍 Shopify']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${tab === key ? 'text-blue-400 border-b-2 border-blue-400' : 'text-white/40 hover:text-white/70'}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'company' && <CompanySection onSaved={() => {}} />}
          {tab === 'shopify' && <ShopifySection />}
        </div>
      </div>
    </div>
  );
}
